/**
 * V3EditorHeader — 编辑器顶栏
 *
 * 使用 WxbCard + WxbButton + WxbTag 组件。
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { WxbButton, WxbCard, WxbTag, WxbSegmented } from '../wxb-ui';
import type { YAxisMode } from '../wxb-ui/GanttChart/types';

const Y_AXIS_OPTIONS: { mode: YAxisMode; label: string }[] = [
  { mode: 'operation', label: '操作' },
  { mode: 'stage-equipment', label: '阶段▸设备' },
  { mode: 'equipment', label: '设备' },
];

export interface V3EditorHeaderProps {
  templateCode: string;
  templateName: string;
  teamName: string | null;
  totalDays: number;
  loading?: boolean;
  onAutoSchedule: () => void;
  /** Current Y-axis grouping mode */
  yAxisMode: YAxisMode;
  /** Y-axis mode change handler */
  onYAxisModeChange: (mode: YAxisMode) => void;
  /** Open the "add stage" dialog */
  onAddStage: () => void;
}

const V3EditorHeader: React.FC<V3EditorHeaderProps> = ({
  templateCode,
  templateName,
  teamName,
  totalDays,
  loading,
  onAutoSchedule,
  yAxisMode,
  onYAxisModeChange,
  onAddStage,
}) => {
  const navigate = useNavigate();

  return (
    <WxbCard
      noPadding
      style={{
        borderRadius: '8px 8px 0 0',
        borderBottom: '1px solid var(--wx-border, #E4EAF1)',
        zIndex: 10,
        flexShrink: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '10px 20px',
          minHeight: 48,
        }}
      >
        {/* Back button */}
        <WxbButton
          variant="ghost"
          size="sm"
          onClick={() => navigate('/process-templates')}
          style={{ flexShrink: 0 }}
        >
          ← 返回
        </WxbButton>

        {/* Separator */}
        <div
          style={{
            width: 1,
            height: 24,
            background: 'var(--wx-border, #E4EAF1)',
            flexShrink: 0,
          }}
        />

        {/* Template info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
          <WxbTag color="blue">{templateCode}</WxbTag>
          <span
            title={templateName}
            style={{
              fontWeight: 600,
              fontSize: 15,
              color: 'var(--wx-ink, #0F1B2D)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {templateName}
          </span>
          {teamName && (
            <span style={{ fontSize: 12, color: 'var(--wx-fg-3, #5A6B7E)', flexShrink: 0 }}>
              {teamName}
            </span>
          )}
          <WxbTag color="neutral">周期 {totalDays} 天</WxbTag>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexShrink: 0 }}>
          {/* Add stage — primary entry so empty templates can be filled */}
          <WxbButton
            variant="primary"
            size="sm"
            onClick={onAddStage}
            style={{ flexShrink: 0 }}
          >
            + 新增阶段
          </WxbButton>

          {/* Separator */}
          <div
            style={{
              width: 1,
              height: 20,
              background: 'var(--wx-border, #E4EAF1)',
              flexShrink: 0,
            }}
          />

          {/* Y-axis mode toggle */}
          <WxbSegmented
            size="sm"
            value={yAxisMode}
            onChange={(v) => onYAxisModeChange(v as YAxisMode)}
            options={Y_AXIS_OPTIONS.map(opt => ({
              label: opt.label,
              value: opt.mode,
            }))}
          />

          {/* Separator */}
          <div
            style={{
              width: 1,
              height: 20,
              background: 'var(--wx-border, #E4EAF1)',
              flexShrink: 0,
            }}
          />

          <WxbButton
            variant="secondary"
            size="sm"
            onClick={onAutoSchedule}
            disabled={loading}
          >
            {loading ? '排程中...' : '自动排程'}
          </WxbButton>
        </div>
      </div>
    </WxbCard>
  );
};

export default V3EditorHeader;
